import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseDirective } from '@egym/ngx-adm-int';
import { Gym, MembershipType } from 'src/app/model/gym';
import { AttributeChangeSchedule } from 'src/app/model/schedule/attribute-change-schedule';
import { GymFieldType } from 'src/app/model/schedule/gym-field-type';
import { ScheduleStatus } from 'src/app/model/schedule/schedule-status';
import { AttributeChangeScheduleService } from 'src/app/service/attribute-change-schedule.service';
import {
  MembershipTypeScheduleUtils,
  MembershipTypeTarget,
} from 'src/app/util/membership-type-schedule-util';
import { AddAttributeChangeScheduleDialogComponent } from './add-attribute-change-schedule-dialog/add-attribute-change-schedule-dialog.component';
import { PromoteMembershipTypeDialogComponent } from './promote-membership-type-dialog/promote-membership-type-dialog.component';

@Component({
  selector: 'gymfinder-attribute-change-schedule',
  templateUrl: './attribute-change-schedule.component.html',
  styleUrls: ['./attribute-change-schedule.component.scss'],
  standalone: false,
})
export class AttributeChangeScheduleComponent
  extends BaseDirective<AttributeChangeSchedule>
  implements OnInit
{
  @Input()
  gym: Gym;

  error: any = undefined;

  loading = false;

  pendingSchedules?: AttributeChangeSchedule[];

  executedSchedules?: AttributeChangeSchedule[];

  constructor(
    snackBar: MatSnackBar,
    private dialog: MatDialog,
    private scheduleService: AttributeChangeScheduleService
  ) {
    super(snackBar);
  }

  ngOnInit(): void {
    this.loadSchedules();
  }

  private loadSchedules() {
    this.loading = true;
    if (this.gym.id) {
      this.scheduleService.getAllByGymId(this.gym.id).subscribe({
        next: n => {
          this.loading = false;
          this.pendingSchedules = (n || []).filter(
            s => s.status === ScheduleStatus.PENDING
          );
          this.executedSchedules = (n || []).filter(
            s => s.status !== ScheduleStatus.PENDING
          );
        },
        error: e => {
          this.loading = false;
          this.error = e;
          this.openSnackBar('There was a problem getting all the schedules!');
        },
      });
    }
  }

  public handleWellpassOnboarding() {
    this.openAddDialog('WELLPASS', 'true');
  }

  public handlePlus1Activation() {
    this.openAddDialog('WELLPASS_PLUS1', 'true');
  }

  public handleWellpassOffboarding() {
    this.openAddDialog('WELLPASS', 'false');
  }

  public handlePlus1Deactivation() {
    this.openAddDialog('WELLPASS_PLUS1', 'false');
  }

  public handleCustom() {
    this.openAddDialog();
  }

  public get canPromoteMembershipType(): boolean {
    return MembershipTypeScheduleUtils.isAvailableFor(this.gym);
  }

  public get canPromoteToUnlimited(): boolean {
    return MembershipTypeScheduleUtils.canPromoteTo(
      this.gym,
      MembershipType.UNLIMITED
    );
  }

  public get canPromoteToCredits(): boolean {
    return MembershipTypeScheduleUtils.canPromoteTo(
      this.gym,
      MembershipType.CREDITS
    );
  }

  public get hasMembershipTypeConflict(): boolean {
    return (
      this.canPromoteMembershipType &&
      MembershipTypeScheduleUtils.hasConflict(this.gym)
    );
  }

  public handlePromoteToUnlimited() {
    this.openPromoteDialog(MembershipType.UNLIMITED);
  }

  public handlePromoteToCredits() {
    this.openPromoteDialog(MembershipType.CREDITS);
  }

  private openPromoteDialog(target: MembershipTypeTarget) {
    const dialogRef = this.dialog.open(PromoteMembershipTypeDialogComponent, {
      data: { gym: this.gym, target },
      width: '640px',
      maxWidth: '95vw',
    });
    dialogRef.afterClosed().subscribe({
      next: n => this.createSchedule(n),
    });
  }

  private openAddDialog(name?: string, value?: string) {
    const schedule: Pick<
      AttributeChangeSchedule,
      'field' | 'name' | 'value'
    > & { gymId: string } = {
      field: GymFieldType.OPTIONS,
      name: name,
      value: value || 'false',
      gymId: this.gym.id,
    };

    const dialogRef = this.dialog.open(
      AddAttributeChangeScheduleDialogComponent,
      {
        data: {
          schedule: schedule,
          gym: this.gym,
        },
      }
    );
    dialogRef.afterClosed().subscribe({
      next: n => this.createSchedule(n),
    });
  }

  private createSchedule(schedule?: AttributeChangeSchedule) {
    if (schedule && this.gym.id) {
      this.loading = true;
      this.error = undefined;
      this.scheduleService.create(schedule).subscribe({
        next: () => {
          this.openSnackBar('The Schedule was created successfully!');
          this.loadSchedules();
        },
        error: e => {
          this.openSnackBar('There was a problem creating the Schedule!');
          this.error = e;
          this.loading = false;
        },
      });
    }
  }

  public handleScheduleDelete(schedule: AttributeChangeSchedule) {
    if (this.gym.id && schedule.id) {
      this.loading = true;
      this.scheduleService.delete(schedule.id).subscribe({
        next: () => {
          this.openSnackBar('The Schedule was deleted successfully!');
          this.loadSchedules();
        },
        error: e => {
          this.error = e;
          this.loading = false;
          this.openSnackBar('There was a problem deleting the schedule!');
        },
      });
    }
  }
}
